import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { Icon, tokens } from 'components/v3';
import {
  AGREE_LABEL_DEFAULT,
  FIELD_TYPES,
  LIMITS,
  fieldTypeIcon,
  fieldTypeLabel,
  isAgreement,
} from '../../shared/fieldTypes';
import { useBuilder } from '../BuilderContext';
import { fieldName, LOCKED_TIP, LockGlyph } from '../desktop/FieldCard';
import ToggleRow from '../fields/ToggleRow';
import { duplicateFieldAction } from '../state/builderReducer';
import { ANSWER_EDITORS } from './editors/AnswerEditors';
import { LAYOUT_EDITORS } from './editors/LayoutEditors';
import {
  Divider,
  ErrorNote,
  Hint,
  Note,
  PanelHead,
  ReadOnlyScope,
  SectionLabel,
  TextAreaField,
  TextField,
} from './parts';
import PrefillSection from './PrefillSection';

// Settings for the selected field (plan §6 A2 "Properties panel", design:
// Properties). Desktop shows it in the right column (or a Drawer below
// 1280px); phones show it full screen in the Edit pane without the head.
//
// A locked field (w9-plan §6.5, BuilderContext `isLocked`) shows its
// summary and the lock note instead of editors. On a `w9` form the
// pre-fill section is left out (guests can't be prefilled). Neither
// changes a regular form.

// The read-only summary of a locked field: label, type, required, help.
const LockedSummary = ({ field }) => {
  const t = tokens(useTheme());
  const rows = [
    ['Label', (field.label || '').trim() || '—'],
    ['Type', fieldTypeLabel(field)],
    ['Required', field.required ? 'Yes' : 'No'],
  ];
  if (field.help) rows.push(['Help text', field.help]);
  if (field.placeholder) rows.push(['Placeholder', field.placeholder]);
  return (
    <div data-fb-locked-summary="">
      <Note icon="info">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <LockGlyph size={13} />
          <strong>{LOCKED_TIP}</strong>
        </span>
        <br />
        This field is fixed by the IRS form. Only your own fields on step one
        can be changed.
      </Note>
      <SectionLabel>Field summary</SectionLabel>
      <dl style={{ margin: 0, display: 'grid', gap: 10, color: t.text }}>
        {rows.map(([key, value]) => (
          <div key={key} style={{ display: 'grid', gap: 2 }}>
            <dt style={{ fontSize: 12, fontWeight: 600, color: t.sub }}>
              {key}
            </dt>
            <dd
              style={{
                margin: 0,
                fontSize: 14,
                color: t.text,
                overflowWrap: 'anywhere',
              }}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

LockedSummary.propTypes = { field: PropTypes.object.isRequired };

// Flips a checkbox between multi-choice and an Agreement (one "I agree"
// box under long-form text). The options are kept either way.
const AgreementStyle = ({ field, setConfig }) => (
  <ToggleRow
    label="Agreement style"
    sub="One “I agree” box with long-form text"
    on={isAgreement(field)}
    onChange={(single) => setConfig('single', single)}
  />
);

AgreementStyle.propTypes = {
  field: PropTypes.object.isRequired,
  setConfig: PropTypes.func.isRequired,
};

// An Agreement's settings (design: Properties, isAgree): no placeholder,
// help text or pre-fill. options[0] is the agreement text.
const AgreementSettings = ({ field, update, setConfig }) => {
  const options = Array.isArray(field.config.options)
    ? field.config.options
    : [];
  return (
    <>
      <TextField
        label="Field label"
        value={field.label}
        maxLength={LIMITS.label}
        onChange={(label) => update({ label })}
      />
      <TextAreaField
        label="Agreement text"
        value={options[0] || ''}
        placeholder="Paste the full waiver, release, rules or terms"
        maxLength={LIMITS.agreementText}
        rows={10}
        style={{ minHeight: 240, lineHeight: 1.55 }}
        onChange={(text) => setConfig('options', [text, ...options.slice(1)])}
      />
      <Hint>
        Line breaks are kept. Long text scrolls in its own box on the form, with
        the checkbox underneath.
      </Hint>
      <TextField
        label="Checkbox text"
        value={field.config.agree_label}
        placeholder={AGREE_LABEL_DEFAULT}
        maxLength={LIMITS.agreeLabel}
        onChange={(value) => setConfig('agree_label', value)}
      />
      <Divider />
      <ToggleRow
        first
        label="Required"
        sub="Must be checked to submit"
        on={!!field.required}
        onChange={(required) => update({ required })}
      />
      <AgreementStyle field={field} setConfig={setConfig} />
    </>
  );
};

AgreementSettings.propTypes = {
  field: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  setConfig: PropTypes.func.isRequired,
};

const AnswerSettings = ({ field, update, setConfig, readOnly, prefill }) => {
  const TypeEditor = ANSWER_EDITORS[field.type];
  const hasPlaceholder =
    FIELD_TYPES[field.type] && FIELD_TYPES[field.type].hasPlaceholder;
  return (
    <>
      <TextField
        label="Field label"
        value={field.label}
        maxLength={LIMITS.label}
        onChange={(label) => update({ label })}
      />
      {hasPlaceholder ? (
        <TextField
          label="Placeholder"
          value={field.placeholder}
          maxLength={LIMITS.placeholder}
          onChange={(placeholder) => update({ placeholder })}
        />
      ) : null}
      <TextField
        label="Help text"
        value={field.help}
        placeholder="Optional guidance below the field"
        maxLength={LIMITS.help}
        onChange={(help) => update({ help })}
      />
      {TypeEditor ? (
        <TypeEditor field={field} update={update} setConfig={setConfig} />
      ) : null}
      <Divider />
      <ToggleRow
        first
        label="Required"
        sub="Must be filled to submit"
        on={!!field.required}
        onChange={(required) => update({ required })}
      />
      {field.type === 'checkbox' ? (
        <AgreementStyle field={field} setConfig={setConfig} />
      ) : null}
      {prefill ? (
        <PrefillSection field={field} onChange={update} disabled={readOnly} />
      ) : null}
    </>
  );
};

AnswerSettings.propTypes = {
  field: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  setConfig: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  prefill: PropTypes.bool,
};

AnswerSettings.defaultProps = { readOnly: false, prefill: true };

const FieldActions = ({ field }) => {
  const { dispatch, saveFieldForReuse, showToast } = useBuilder();
  return (
    <>
      <Divider />
      <ButtonV2
        style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }}
        onClick={() => saveFieldForReuse(field)}
        icon={<Icon name="bookmark" size={15} color="currentColor" />}
      >
        Save to Saved Fields
      </ButtonV2>
      <div style={{ display: 'flex', gap: 8 }}>
        <ButtonV2
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={() => {
            dispatch(duplicateFieldAction(field.id));
            showToast('Field duplicated');
          }}
          icon={<Icon name="copy" size={15} color="currentColor" />}
        >
          Duplicate
        </ButtonV2>
        <ButtonV2
          danger
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={() => {
            dispatch({ type: 'REMOVE_FIELD', id: field.id });
            showToast('Field deleted');
          }}
          icon={<Icon name="trash-2" size={15} color="currentColor" />}
        >
          Delete
        </ButtonV2>
      </div>
    </>
  );
};

FieldActions.propTypes = { field: PropTypes.object.isRequired };

const FieldProperties = ({ field, showHeader }) => {
  const { dispatch, readOnly, state, isLocked, kind } = useBuilder();
  const locked = !!isLocked && isLocked(field.id);
  const update = (patch) =>
    dispatch({ type: 'UPDATE_FIELD', id: field.id, patch });
  const setConfig = (key, value) => update({ config: { [key]: value } });
  const Layout = LAYOUT_EDITORS[field.type];
  let Settings = AnswerSettings;
  if (Layout) Settings = Layout;
  else if (isAgreement(field)) Settings = AgreementSettings;
  return (
    <div data-tour="properties">
      {showHeader ? (
        <PanelHead
          icon={fieldTypeIcon(field)}
          title={fieldName(field)}
          sub={`${fieldTypeLabel(field)} field${
            locked ? ` · ${LOCKED_TIP}` : ''
          }`}
        />
      ) : null}
      <div style={{ padding: showHeader ? 18 : 0 }}>
        <ErrorNote>{state.errors[`definition.${field.id}`]}</ErrorNote>
        {locked ? (
          <LockedSummary field={field} />
        ) : (
          <>
            <ReadOnlyScope readOnly={readOnly}>
              <Settings
                field={field}
                update={update}
                setConfig={setConfig}
                readOnly={readOnly}
                prefill={kind !== 'w9'}
              />
            </ReadOnlyScope>
            {readOnly ? null : <FieldActions field={field} />}
          </>
        )}
      </div>
    </div>
  );
};

FieldProperties.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  showHeader: PropTypes.bool,
};

FieldProperties.defaultProps = { showHeader: true };

export default FieldProperties;
